import React, { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { useProfile } from '../hooks/useProfile'
import { useToast } from './Toast'
import LoadingSpinner from './LoadingSpinner'
import { FirebaseStorageService } from '../services/FirebaseStorageService'
import { isTrialExpired, getTrialDaysRemaining } from '../services/FirestoreService'
import ImageUpload from './ImageUpload'
import FoodGenreSelector from './FoodGenreSelector'
import DietaryPreferences from './DietaryPreferences'
import PaymentForm from './PaymentForm'
import RecipeDiscovery from './RecipeDiscovery'
import SavedRecipes from './SavedRecipes'
import MealPlanner from './MealPlanner'
import './Dashboard.css'

const Dashboard: React.FC = () => {
  const { user, signOut } = useAuth()
  const { profile, loading, updateProfile } = useProfile()
  const { showToast } = useToast()
  const [selectedGenres, setSelectedGenres] = useState<string[]>([])
  const [dietaryPreferences, setDietaryPreferences] = useState('')
  const [uploadedImages, setUploadedImages] = useState<string[]>([])
  const [showPayment, setShowPayment] = useState(false)
  const [activeTab, setActiveTab] = useState<'setup' | 'recipes' | 'saved' | 'meal-plan'>('setup')

  // Calculate trial days remaining
  const trialDaysRemaining = profile?.trial_start_date ? getTrialDaysRemaining(profile) : null
  const isOnTrial = profile?.trial_start_date && !isTrialExpired(profile)

  useEffect(() => {
    if (profile) {
      setSelectedGenres(profile.food_genres || [])
      setDietaryPreferences(profile.dietary_preferences || '')
      // Show payment if:
      // - User has never paid (payment_method is 'none' or undefined)
      // - OR user is on trial AND trial has expired
      const isTrialUser = profile.payment_method === 'trial'
      const trialExpired = isTrialUser && isTrialExpired(profile)
      const neverPaid = !profile.payment_method || profile.payment_method === 'none'
      setShowPayment(neverPaid || trialExpired)
    }
  }, [profile])


  // Load user-specific active tab and save changes
  useEffect(() => {
    if (user) {
      const savedTab = localStorage.getItem(`dashboard-active-tab-${user.id}`)
      // Validate that the saved tab is a valid option before using it
      const validTabs = ['setup', 'recipes', 'saved', 'meal-plan'] as const
      if (savedTab && validTabs.includes(savedTab as typeof validTabs[number])) {
        setActiveTab(savedTab as 'setup' | 'recipes' | 'saved' | 'meal-plan')
      }
    }
  }, [user])

  // Save active tab to localStorage when it changes
  useEffect(() => {
    if (user) {
      localStorage.setItem(`dashboard-active-tab-${user.id}`, activeTab)
    }
  }, [activeTab, user])

  // Load existing uploaded images when component mounts
  useEffect(() => {
    const loadExistingImages = async () => {
      if (!user) return

      try {
        const imageUrls = await FirebaseStorageService.getUserImages(user.id)
        setUploadedImages(imageUrls)
      } catch (error) {
        console.error('Error loading existing images:', error)
      }
    }

    loadExistingImages()
  }, [user])

  const handleGenresChange = (genres: string[]) => {
    if (process.env.NODE_ENV === 'development') {
      console.log('🔄 Dashboard: handleGenresChange called with:', genres)
    }
    setSelectedGenres(genres)
  }

  const handleSavePreferences = async () => {
    try {
      if (process.env.NODE_ENV === 'development') {
        console.log('💾 Saving preferences with genres:', selectedGenres)
      }
      await updateProfile({
        food_genres: selectedGenres,
        dietary_preferences: dietaryPreferences
      })
      showToast('Preferences saved successfully!', 'success')
    } catch (error) {
      console.error('Error saving preferences:', error)
      showToast('Error saving preferences. Please try again.', 'error')
    }
  }

  const handlePaymentSuccess = () => {
    setShowPayment(false)
    showToast('Payment successful! Welcome to Food at Home!', 'success')
  }

  const handleImagesUploaded = (imageUrls: string[]) => {
    setUploadedImages(imageUrls)
  }

  const handleSignOut = async () => {
    try {
      await signOut()
    } catch (error) {
      console.error('Error signing out:', error)
    }
  }

  if (loading) {
    return <LoadingSpinner message="Loading your profile..." />
  }

  if (showPayment) {
    return <PaymentForm onPaymentSuccess={handlePaymentSuccess} />
  }

  return (
    <div className="dashboard">
      <header className="dashboard-header">
        <div className="header-content">
          <h1>Food at Home</h1>
          <div className="user-info">
            <span>Welcome, {user?.user_metadata?.full_name || user?.email}</span>
            <button 
              onClick={handleSignOut} 
              className="sign-out-button"
              aria-label="Sign out of your account"
            >
              Sign Out
            </button>
          </div>
        </div>
      </header>

      {isOnTrial && (
        <div 
          className="trial-banner" 
          role="status"
          aria-live="polite"
          style={{
          background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          padding: '10px 20px',
          textAlign: 'center',
          fontSize: '14px'
        }}>
          {trialDaysRemaining === 1
            ? 'Your free trial ends tomorrow!'
            : `${trialDaysRemaining} days left in your free trial`}
          {' '}<span style={{ opacity: 0.8 }}>| Upgrade to continue after trial</span>
        </div>
      )}

      <main className="dashboard-main">
        <div className="dashboard-container">
          {profile?.has_paid && (
            <div className="dashboard-tabs" role="tablist" aria-label="Dashboard navigation">
              <button
                className={`tab-button ${activeTab === 'setup' ? 'active' : ''}`}
                onClick={() => setActiveTab('setup')}
                role="tab"
                aria-selected={activeTab === 'setup'}
                aria-controls="setup-panel"
                id="setup-tab"
              >
                🔧 Setup
              </button>
              <button
                className={`tab-button ${activeTab === 'recipes' ? 'active' : ''}`}
                onClick={() => setActiveTab('recipes')}
                disabled={uploadedImages.length === 0}
                role="tab"
                aria-selected={activeTab === 'recipes'}
                aria-controls="recipes-panel"
                id="recipes-tab"
                aria-disabled={uploadedImages.length === 0}
              >
                🍳 Recipe Discovery
              </button>
              <button
                className={`tab-button ${activeTab === 'saved' ? 'active' : ''}`}
                onClick={() => setActiveTab('saved')}
                role="tab"
                aria-selected={activeTab === 'saved'}
                aria-controls="saved-panel"
                id="saved-tab"
              >
                ❤️ Saved Recipes
              </button>
              <button
                className={`tab-button ${activeTab === 'meal-plan' ? 'active' : ''}`}
                onClick={() => setActiveTab('meal-plan')}
                role="tab"
                aria-selected={activeTab === 'meal-plan'}
                aria-controls="meal-plan-panel"
                id="meal-plan-tab"
              >
                📅 Meal Planning
                {profile?.subscription_tier !== 'premium' && <span className="premium-badge">Premium</span>}
              </button>
            </div>
          )}

          {activeTab === 'setup' && (
            <div 
              className="setup-content"
              role="tabpanel"
              id="setup-panel"
              aria-labelledby="setup-tab"
            >
              <div className="welcome-section">
                <h2>Set Up Your Food Preferences</h2>
                <p>
                  Complete your profile to get personalized recipe recommendations based on
                  your ingredients and preferences.
                </p>
              </div>

              <div className="setup-sections">
                <section className="setup-section">
                  <ImageUpload
                    onImagesUploaded={handleImagesUploaded}
                    maxImages={10}
                  />
                </section>

                <section className="setup-section">
                  <FoodGenreSelector
                    selectedGenres={selectedGenres}
                    onGenresChange={handleGenresChange}
                  />
                </section>

                <section className="setup-section">
                  <DietaryPreferences
                    preferences={dietaryPreferences}
                    onPreferencesChange={setDietaryPreferences}
                  />
                </section>

                <div className="save-section">
                  <button
                    onClick={handleSavePreferences}
                    className="save-button"
                    disabled={loading}
                    aria-label="Save your food preferences and dietary restrictions"
                  >
                    {loading ? 'Saving...' : 'Save Preferences'}
                  </button>
                </div>
              </div>

              {!profile?.has_paid && (
                <div className="next-steps">
                  <h3>What's Next?</h3>
                  <div className="next-steps-content">
                    <div className="step">
                      <div className="step-icon">🔍</div>
                      <div className="step-text">
                        <h4>Recipe Discovery</h4>
                        <p>Upload ingredient photos above to get recipes requiring 0-2 additional ingredients max</p>
                      </div>
                    </div>
                    <div className="step">
                      <div className="step-icon">🍽️</div>
                      <div className="step-text">
                        <h4>Meal Planning</h4>
                        <p>Plan your weekly meals with suggested recipes</p>
                      </div>
                    </div>
                    <div className="step">
                      <div className="step-icon">🛒</div>
                      <div className="step-text">
                        <h4>Smart Shopping</h4>
                        <p>Only shop for 1-2 ingredients when needed - maximize your kitchen's potential</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'recipes' && (
            <div 
              className="recipe-content"
              role="tabpanel"
              id="recipes-panel"
              aria-labelledby="recipes-tab"
            >
              <RecipeDiscovery />
            </div>
          )}

          {activeTab === 'saved' && (
            <div 
              className="saved-content"
              role="tabpanel"
              id="saved-panel"
              aria-labelledby="saved-tab"
            >
              <SavedRecipes />
            </div>
          )}

          {activeTab === 'meal-plan' && (
            <div 
              className="meal-plan-content"
              role="tabpanel"
              id="meal-plan-panel"
              aria-labelledby="meal-plan-tab"
            >
              <MealPlanner />
            </div>
          )}
        </div>
      </main>
    </div>
  )
}

export default Dashboard